import React, { Component } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import cellEditFactory from 'react-bootstrap-table2-editor';
import Loader from 'react-loader-spinner';

import './Table.css';

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE_PER_PAGE = 10;
const ALL_RECORDS_PAGE_SIZE = -1;

const DEFAULT_ROW_MAPPER = (record) => record;

class Table extends Component {
    constructor(props) {
        super(props);
        this._keyField = props.keyField;
        this._columns = props.columns;
        this._selectionMode = props.selectionMode;
        this._editionMode = props.editionMode;
        this._recordsProvider = props.recordsProvider;
        this._recordsCountProvider = props.recordsCountProvider;
        this._rowMapper = props.rowMapper != null ? this.props.rowMapper : DEFAULT_ROW_MAPPER;

        this._externalHandlers = {
            edit: props.onEdit,
            select: props.onSelect,
            load: props.onLoad
        }

        this.state = {
            page: props.page == null ? DEFAULT_PAGE : props.page,
            sizePerPage: props.sizePerPage == null ? DEFAULT_SIZE_PER_PAGE : props.sizePerPage,
            data: [],
            totalSize: 0,
            selected: [],
            marked: []
        };

        this.editRow = this.editRow.bind(this);
        this.refresh = this.refresh.bind(this);
        this.markRow = this.markRow.bind(this);
        this._handleTableChange = this._handleTableChange.bind(this);
        this._handleEdit = this._handleEdit.bind(this);
		this._handleSelect = this._handleSelect.bind(this);
		this._handleSelectAll = this._handleSelectAll.bind(this);
    }

    get keyField() {
        return this._keyField;
    }

    set keyField(keyField) {
        this._keyField = keyField;
    }

    get columns() {
        return this._columns;
    }

    set columns(columns) {
        this._columns = columns;
    }

    get page() {
        return this.state.page;
    }

    set page(page) {
        if (page < 0) {
            throw new Error("Page number out of range.");
        }

        if (page > this.getTotalSize() / this.getSizePerPage()) {
            throw new Error("Page number out of range.")
        }

        this.setState({ page: page });
    }

    get sizePerPage() {
        return this.state.sizePerPage;
    }

    set sizePerPage(sizePerPage) {
        if (sizePerPage === ALL_RECORDS_PAGE_SIZE) {
            this.setState({ sizePerPage: this.getTotalSize });
        }

        if (sizePerPage < ALL_RECORDS_PAGE_SIZE) {
            throw new Error(`Size per page can not be less than ${ALL_RECORDS_PAGE_SIZE}.`)
        }

        this.setState({ sizePerPage: this.sizePerPage });
    }

    get data() {
        if (this.state.data == null) {
            return [];
        }

        return this.state.data;
    }

    set data(data) {
        this.setState({ data: data });
    }

    get totalSize() {
        return this.state.totalSize;
    }

    get selectedIds() {
        if (this.state.selected == null) {
            return [];
        }

        return this.state.selected;
    }

    get selectedRows() {
        if (this.state.selected == null) {
            return [];
        }

        return this.state.data.filter(row => this.state.selected.includes(row[this._keyField]));
    }

    editRow(rowId, newData) {
        const result = this.state.data.map((row) => {
            if (row.id === rowId) {
                row = newData;
            }

            return row;
        });

        this.setState({ data: result });
    }

    markRow(rowId) {
        let markedIds = this.state.marked;
        markedIds = [...markedIds, rowId];
        
        this.setState({ marked: markedIds });
    }

    unmarkRow(rowId) {
        let markedIds = this.state.marked;
        markedIds = markedIds.filter(id => id !== rowId);

        this.setState({ marked: markedIds });
    }

    refresh() {
        this._handleContentLoad(this.state);
    }

    _handleTableChange(type, data) {
		if (type === 'cellEdit') {
			this._handleEdit(data);
		} else {
			this._handleContentLoad(data);
        }
	}

    _handleEdit({ data, cellEdit: { rowId, dataField, newValue } }) {
        const result = data.map((row) => {
            if (row.id === rowId) {
                row[dataField] = newValue;
            }

            return row;
        });

        this.setState({ data: result });

        if (this._externalHandlers.edit != null) {
            let event = new Event('edit');
            event.detail = data;

            this._externalHandlers.edit(event);
        }
    }

    _handleContentLoad({ page, sizePerPage }) {
        this.setState({
            loaded: false,
            page: page,
            sizePerPage: sizePerPage,
            selected: [],
            marked: []
        });

        if (page == null) {
            page = 1;
        }

        if (sizePerPage == null) {
            sizePerPage = 10;
        }

        this._recordsProvider(page, sizePerPage, function(data) {
            var mappedData = [];
            if (data != null) {
                mappedData = data.map(this._rowMapper);
            }

            this.setState({
                loaded: true,
                data: mappedData
            });
        }.bind(this));

        this._recordsCountProvider(function(count) {
            this.setState({
                totalSize: count
            });
        }.bind(this));

    }

    _handleSelect(row, isSelect) {
        let selectedIds = this.state.selected;

        if (isSelect) {
            selectedIds = [...selectedIds, row.id];
        } else {
            selectedIds = selectedIds.filter(id => id !== row.id);
        }

        this.setState({ selected: selectedIds }, () => {
            if (this._externalHandlers.select != null) {
                let event = new Event('select');
                event.detail = { selected: selectedIds };
    
                this._externalHandlers.select(event);
            }
        });
    }

    _handleSelectAll(isSelect, rows) {
        let selectedIds = [];
        if (isSelect) {
            selectedIds = rows.map(row => row.id);
        }

        this.setState({ selected: selectedIds }, () => {
            if (this._externalHandlers.select != null) {
                let event = new Event('select');
                event.detail = { selected: selectedIds };
    
                this._externalHandlers.select(event);
            }
        });
    }

    componentDidMount() {
        document.querySelectorAll('.react-bootstrap-table-pagination a').forEach((item) => {
            item.addEventListener('click', (event) => {
                event.preventDefault();
            });
        });
    }

    render() {
		const { data, sizePerPage, page, totalSize, selected } = this.state;
		
        let selectionSettings = null;
        if (this._selectionMode != null) {
            selectionSettings = {
                mode: this._selectionMode,
                selected: selected,
                onSelect: this._handleSelect,
                onSelectAll: this._handleSelectAll
            };

        }

        let editionSettings = null;
        if (this._editionMode) {
            editionSettings = {
                mode: this._editionMode,
                blurToSave: true
            };
        }

        let defaultSorting = [{
            dataField: this._keyField,
            order: 'asc'
        }];

        let defaultRowClasses = (row, rowIndex) => {
            const { marked } = this.state;
            if (marked.includes(row[this._keyField])) {
                return 'bg-danger';
            }

            return null;
        }

        return(
            <div>
                <div className="table-processing" hidden={ this.state.loaded }>
                    <div>
                        <Loader type="TailSpin" color="rgb(56, 56, 56)" height={25} width={25}/>
                        <div className="loader-text">Loading...</div>
                    </div>
                </div>
                <BootstrapTable
                    remote
                    keyField={ this._keyField }
                    data={ data }
                    defaultSorted={ defaultSorting }
                    columns={ this._columns }
                    pagination={ paginationFactory({ page, sizePerPage, totalSize }) }
                    cellEdit={ editionSettings != null ? cellEditFactory(editionSettings) : null }
                    selectRow={ selectionSettings }
                    onTableChange={ this._handleTableChange }
                    rowClasses={ defaultRowClasses }
                />
            </div>
        );
    }
}

export default Table;


// WEBPACK FOOTER //
// ./src/_components/table/Table.jsx